import { useState } from "react";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DateTimePicker from "@components/ui/selection/DateTimePicker";
import RestitutionChangeConfirmModal from "@components/modals/RestitutionChangeConfirmModal";
import ForceConfirmModal from "@components/modals/ForceConfirmModal";
import { ordresApi } from "@services/api/ordres";
import { useOrdreStore } from "@store/ordreStore";
import { usePlanningStore } from "@store/planningStore";
import { useAtelierStore } from "@store/atelierStore";
import { showNotification } from "context/notification.service";
import type { Ordre } from "../../types/ordre";

/** Fenêtre réception → restitution minimale (un créneau de 15 min), comme OrdreModal. */
const MIN_WINDOW_SECONDS = 15 * 60;

interface Props {
  ordre: Ordre;
  onClose: () => void;
  onSaved?: (updated: Ordre) => void;
}

/**
 * Horaires de réception / restitution modifiables depuis la fiche d'ordre (Héric
 * 2026-10-13 : le planning ouvre désormais la fiche, plus la modale « Modifier RDV »).
 * Même enregistrement que la modale : mêmes règles de fenêtre, même question
 * « prévenir le client » quand une date déjà fixée change, même « Forcer » quand le
 * serveur refuse le créneau.
 */
export default function OrdreHorairesModal({ ordre, onClose, onSaved }: Props) {
  const [reception, setReception] = useState<number | null>(ordre.reception_date?.seconds || null);
  const [restitution, setRestitution] = useState<number | null>(ordre.restitution?.seconds || null);
  const [saving, setSaving] = useState(false);
  const [confirmKind, setConfirmKind] = useState<"restitution" | "reception" | null>(null);
  const [forceMessage, setForceMessage] = useState<string | null>(null);
  // 409 = refus non forçable (présence du véhicule < durée des travaux…).
  const [forceAllowed, setForceAllowed] = useState(true);
  const [pendingForce, setPendingForce] = useState(false);

  const oldRec = ordre.reception_date?.seconds || null;
  const oldRest = ordre.restitution?.seconds || null;
  const receptionNotifiable = !!oldRec && !!reception && reception !== oldRec;
  const restitutionNotifiable = !!oldRest && !!restitution && restitution !== oldRest;
  const changed = reception !== oldRec || restitution !== oldRest;

  const save = async (comment?: string, notify?: boolean, force?: boolean) => {
    setSaving(true);
    try {
      const updated = await ordresApi.update(ordre.id, {
        reception_date: reception ? ({ seconds: reception } as any) : ordre.reception_date,
        restitution: restitution ? ({ seconds: restitution } as any) : ordre.restitution,
        ...(force ? { force: true } : {}),
      });
      if (restitutionNotifiable && notify !== false) {
        try {
          await ordresApi.notifyRestitutionChange(ordre.id, {
            old_date_seconds: oldRest || undefined,
            new_date_seconds: restitution!,
            comment: comment || undefined,
            notify,
          });
        } catch (e) {
          console.error("Error notifying restitution change:", e);
        }
      }
      if (receptionNotifiable && notify !== false) {
        try {
          await ordresApi.notifyReceptionChange(ordre.id, {
            old_date_seconds: oldRec || undefined,
            new_date_seconds: reception!,
            comment: comment || undefined,
            notify,
          });
        } catch (e) {
          console.error("Error notifying reception change:", e);
        }
      }
      useOrdreStore.getState().updateOrdre(updated);
      const ps = usePlanningStore.getState();
      if (ps._fetchedOrgId) ps.fetchAppointments(ps._fetchedOrgId, true);
      useAtelierStore.getState().invalidateCarWindows(ordre.id);
      showNotification({ type: "success", message: "Horaires enregistrés" });
      onSaved?.(updated);
      onClose();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const unforceable = e?.response?.status === 409;
      if (detail && (!force || unforceable)) {
        setForceAllowed(!unforceable);
        setForceMessage(String(detail));
      } else {
        showNotification({ type: "error", message: detail ? String(detail) : "Erreur lors de l'enregistrement" });
      }
    } finally {
      setSaving(false);
      setConfirmKind(null);
      setPendingForce(false);
    }
  };

  const proceed = (force = false) => {
    if (restitutionNotifiable || receptionNotifiable) {
      setPendingForce(force);
      setConfirmKind(restitutionNotifiable ? "restitution" : "reception");
    } else {
      save(undefined, undefined, force);
    }
  };

  return (
    <>
      <BaseModal title="Horaires du rendez-vous" onClose={onClose} width="max-w-xl">
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <DateTimePicker
                label="Date réception"
                seconds={reception}
                onChange={(s) => {
                  setReception(s);
                  if (restitution && restitution < s + MIN_WINDOW_SECONDS) setRestitution(s + MIN_WINDOW_SECONDS);
                }}
              />
            </div>
            <div className="flex-1">
              <DateTimePicker
                label="Date restitution"
                seconds={restitution}
                onChange={(s, forced) => {
                  const min = reception ? reception + MIN_WINDOW_SECONDS : 0;
                  setRestitution(forced ? s : Math.max(s, min));
                }}
              />
            </div>
          </div>
          {!restitution && (
            <span className="text-h4 text-red-500">La date de restitution est obligatoire</span>
          )}
          <div className="flex flex-row justify-end gap-2">
            <SecondaryButton variant="small" onClick={onClose}>
              Annuler
            </SecondaryButton>
            <Button
              variant="small"
              label={saving ? "Enregistrement…" : "Enregistrer"}
              onClick={() => proceed(false)}
              disabled={saving || !changed || !restitution}
            />
          </div>
        </div>
      </BaseModal>
      {confirmKind && (
        <RestitutionChangeConfirmModal
          kind={confirmKind}
          oldSeconds={(confirmKind === "restitution" ? oldRest : oldRec) || undefined}
          newSeconds={(confirmKind === "restitution" ? restitution : reception)!}
          onClose={() => { setConfirmKind(null); setPendingForce(false); }}
          onConfirm={(comment, notify) => save(comment, notify, pendingForce)}
          saving={saving}
        />
      )}
      {forceMessage && (
        <ForceConfirmModal
          title="Créneau non conforme"
          message={forceMessage}
          confirmLabel="Forcer ce RDV"
          allowForce={forceAllowed}
          onCancel={() => setForceMessage(null)}
          onConfirm={() => { setForceMessage(null); proceed(true); }}
        />
      )}
    </>
  );
}
